import { useCallback, useState } from 'react';

import { IImageRulesConfig } from '../constants/imageRules';
import projectsService from '../services/projects-service';

interface IImageRulesCacheEntry {
  data: IImageRulesConfig | null;
  error: boolean;
}

// Module-level cache keeps responses for the lifetime of the page session.
const imageRulesCache = new Map<string, IImageRulesCacheEntry>();

const buildCacheKey = (
  projectId: string | null | undefined,
  tenantId: string | null | undefined,
): string => `${projectId ?? ''}|${tenantId ?? ''}`;

interface IUseProjectImageRulesState {
  data: IImageRulesConfig | null;
  loading: boolean;
  error: boolean;
}

const INITIAL_STATE: IUseProjectImageRulesState = {
  data: null,
  loading: false,
  error: false,
};

/**
 * Fetches project image rules on demand. The API is not called until
 * `loadImageRules` is invoked, and successful responses are cached while the
 * page remains open.
 */
export const useProjectImageRules = (
  projectId: string | null | undefined,
  tenantId: string | null | undefined,
) => {
  const [state, setState] = useState<IUseProjectImageRulesState>(INITIAL_STATE);

  const loadImageRules = useCallback(
    async (options?: { force?: boolean }) => {
      const cacheKey = buildCacheKey(projectId, tenantId);

      if (!projectId) {
        setState({ data: null, loading: false, error: true });
        return;
      }

      if (!options?.force && imageRulesCache.has(cacheKey)) {
        const cached = imageRulesCache.get(cacheKey)!;
        setState({
          data: cached.data,
          loading: false,
          error: cached.error,
        });
        return;
      }

      setState((previous) => ({ ...previous, loading: true, error: false }));

      try {
        const data = (await projectsService.getProjectImageRules(
          projectId,
          tenantId,
        )) as IImageRulesConfig;

        imageRulesCache.set(cacheKey, { data, error: false });
        setState({ data, loading: false, error: false });
      } catch {
        imageRulesCache.set(cacheKey, { data: null, error: true });
        setState({ data: null, loading: false, error: true });
      }
    },
    [projectId, tenantId],
  );

  const retry = useCallback(() => {
    return loadImageRules({ force: true });
  }, [loadImageRules]);

  return {
    ...state,
    loadImageRules,
    retry,
  };
};

export default useProjectImageRules;
